import React from 'react';
import { motion } from 'framer-motion';
import { Phone, AlertTriangle, CircleDot, Wind, Gauge, ArrowRight } from 'lucide-react';
import { PHONES } from '../content';

const SCENARIOS = [
  {
    icon: AlertTriangle,
    situation: 'Lastiğim Patladı',
    urgency: 'Acil Müdahale',
    desc: 'Bulunduğunuz yere en yakın ekip yola çıkıyor. Yerinde tamir, servise gerek yok.',
    cta: 'Hemen Ara — Bekleme',
    color: 'var(--red)',
    bg: 'rgba(229,51,51,.1)',
    border: 'rgba(229,51,51,.25)',
    glowBg: 'rgba(229,51,51,.06)',
  },
  {
    icon: CircleDot,
    situation: 'Stepnem Yok',
    urgency: 'Lastik Getiriyoruz',
    desc: 'Stepneniz yoksa endişelenmeyin. Aracınızın ebatına uygun lastik getirip yerinde takıyoruz.',
    cta: 'Lastik Getirtin',
    color: 'var(--blue)',
    bg: 'var(--blue-dim)',
    border: 'var(--blue-border)',
    glowBg: 'rgba(74,143,245,.04)',
  },
  {
    icon: Wind,
    situation: 'Basıncım Düştü',
    urgency: 'Yerinde Şişirme',
    desc: 'Lastik basıncı düştü mü? Gelip doğru basınca getiriyoruz. Sibop arızası da çözüyoruz.',
    cta: 'Yardım İste',
    color: 'var(--green)',
    bg: 'var(--green-dim)',
    border: 'var(--green-border)',
    glowBg: 'rgba(16,185,129,.04)',
  },
  {
    icon: Gauge,
    situation: 'Yolda Kaldım',
    urgency: 'Mobil Servis',
    desc: 'Nerede kaldığınız fark etmez. Konum gönderin, ortalama 30 dakikada yanınızdayız.',
    cta: 'Konum Gönder',
    color: '#ff9500',
    bg: 'rgba(255,149,0,.1)',
    border: 'rgba(255,149,0,.25)',
    glowBg: 'rgba(255,149,0,.04)',
  },
];

export default function ScenarioServices() {
  return (
    <section
      data-testid="scenario-services"
      className="section"
      style={{
        background: 'var(--bg)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div className="wrap">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
          <div className="pill pill-red" style={{ marginBottom: '1rem' }}>
            Durumunuzu Seçin
          </div>
          <h2 style={{
            fontFamily: "'Space Grotesk',sans-serif", fontWeight: 800,
            fontSize: 'clamp(1.6rem, 4vw, 2.75rem)',
            color: 'var(--text)', lineHeight: 1.08,
            letterSpacing: '-.035em', marginBottom: '.625rem',
          }}>
            Ne Oldu? <span style={{ color: 'var(--red)' }}>Söyleyin, Çözelim.</span>
          </h2>
          <p style={{ fontSize: '.9rem', color: 'var(--text2)', maxWidth: 460, margin: '0 auto' }}>
            Durumunuza tıklayın — size özel çözümü hemen sunalım.
          </p>
        </div>

        {/* Scenario cards — NOT a grid. Stacked with emphasis. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: 800, margin: '0 auto' }}>
          {SCENARIOS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.situation}
                data-testid={`scenario-${i}`}
                initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: .5, delay: i * .08 }}
                style={{
                  background: `linear-gradient(135deg, ${s.glowBg} 0%, var(--card) 100%)`,
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--rl)',
                  padding: '1.75rem 2rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1.5rem',
                  transition: 'border-color .25s, transform .18s, box-shadow .25s',
                  cursor: 'default',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = s.border;
                  e.currentTarget.style.transform = 'translateX(6px)';
                  e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,.35)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.transform = 'translateX(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Icon */}
                <div style={{
                  width: 56, height: 56, borderRadius: 14,
                  background: s.bg, border: `1px solid ${s.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: s.color, flexShrink: 0,
                }}>
                  <Icon size={24} strokeWidth={1.75} />
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.625rem', marginBottom: '.35rem', flexWrap: 'wrap' }}>
                    <h3 style={{
                      fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700,
                      fontSize: '1.1rem', color: 'var(--text)',
                    }}>
                      "{s.situation}"
                    </h3>
                    <span style={{
                      fontSize: '.65rem', fontWeight: 700,
                      color: s.color, textTransform: 'uppercase',
                      letterSpacing: '.06em',
                      background: s.bg, border: `1px solid ${s.border}`,
                      borderRadius: 100, padding: '.15rem .5rem',
                    }}>{s.urgency}</span>
                  </div>
                  <p style={{ fontSize: '.85rem', color: 'var(--text2)', lineHeight: 1.6 }}>
                    {s.desc}
                  </p>
                </div>

                {/* CTA */}
                <a
                  href={PHONES.main_tel}
                  className="btn btn-primary btn-sm num"
                  style={{
                    gap: '.4rem', flexShrink: 0, whiteSpace: 'nowrap',
                    background: s.color === 'var(--red)' ? 'var(--red)' : s.bg,
                    color: s.color === 'var(--red)' ? '#fff' : s.color,
                    border: s.color === 'var(--red)' ? 'none' : `1px solid ${s.border}`,
                  }}
                >
                  <Phone size={13} /> {s.cta}
                </a>
              </motion.div>
            );
          })}
        </div>

        {/* Urgency microcopy */}
        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '.4rem',
            fontSize: '.78rem', color: 'var(--text2)',
          }}>
            <span className="live" style={{
              width: 6, height: 6, borderRadius: '50%',
              background: 'var(--green)', display: 'inline-block',
            }} />
            Şu an müsaitiz — beklemeyin, hemen arayın
          </span>
        </div>
      </div>

      <style>{`
        @media(max-width:640px){
          [data-testid^="scenario-"] {
            flex-direction: column !important;
            align-items: flex-start !important;
            text-align: left;
          }
          [data-testid^="scenario-"] a {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </section>
  );
}
